/**
 * useVoiceCapture — unified voice capture for the Record button.
 *
 * Prefers browser-direct AWS Transcribe (higher quality, consistent across
 * browsers) when the Identity Pool is configured and Web Audio is available,
 * and falls back to the browser Web Speech API otherwise. Both underlying
 * hooks are always called (React rules of hooks); only the selected engine's
 * controls are returned, so the unselected one stays idle.
 */

import { useDictation } from './useDictation';
import { useTranscribeCapture } from './useTranscribeCapture';

export type VoiceEngine = 'transcribe' | 'webspeech' | null;

export interface UseVoiceCaptureReturn {
  engine: VoiceEngine;
  isListening: boolean;
  isSupported: boolean;
  toggle: () => void;
  stop: () => void;
}

/** Choose the engine from each backend's availability (pure — unit-tested). */
export function pickEngine(transcribeSupported: boolean, dictationSupported: boolean): VoiceEngine {
  if (transcribeSupported) return 'transcribe';
  if (dictationSupported) return 'webspeech';
  return null;
}

interface UseVoiceCaptureOptions {
  onTranscript: (transcript: string) => void;
  lang?: string;
  onError?: (error: Error) => void;
}

export function useVoiceCapture({ onTranscript, lang, onError }: UseVoiceCaptureOptions): UseVoiceCaptureReturn {
  const transcribe = useTranscribeCapture({ onTranscript, lang, onError });
  const dictation = useDictation({ onTranscript, lang });

  const engine = pickEngine(transcribe.isSupported, dictation.isSupported);
  const active = engine === 'transcribe' ? transcribe : engine === 'webspeech' ? dictation : null;

  return {
    engine,
    isSupported: engine !== null,
    // Reflect either engine, and stop BOTH — so a selection change can never
    // strand a live session on the unselected engine (each stop is a safe
    // no-op when idle). toggle drives only the selected engine.
    isListening: transcribe.isListening || dictation.isListening,
    toggle: active?.toggle ?? (() => {}),
    stop: () => {
      transcribe.stop();
      dictation.stop();
    },
  };
}
